import type { HeroId, ProjectileKind } from './types.ts';

const DEG = Math.PI / 180;
/** hitbox de los héroes ×1.25 (Bowser no: la suya ya es grande) */
const HITBOX_SCALE = 1.25;

export interface StrikeDef {
  duration: number;
  activeStart: number;
  activeEnd: number;
  range: number;
  arc: number;
  damage: number;
  knockback: number;
  hitstun: number;
  cooldownTicks: number;
  dashTicks: number;
  dashSpeed: number;
  invulnTicks: number;
  /** 0 = single hit, otherwise the hit list resets every N ticks inside the active window. */
  rehitEvery: number;
  heavy: boolean;
  /** false: el héroe puede caminar durante la habilidad (p. ej. el giro de Link) */
  moveLock: boolean;
  /** Durante el dash, chocar con un enemigo corta el salto y dispara el golpe ya. */
  slamOnContact: boolean;
  /**
   * Sólo los golpes `stun` aplican `hitstun` (interrumpen y bloquean al rival).
   * El resto hace daño y empuja, pero no corta habilidades ni ataques.
   */
  stun: boolean;
  /** Imparable (como la R de Sion): durante la habilidad los aturdimientos no lo frenan. */
  unstoppable: boolean;
  /**
   * Hitbox de barrido: un rectángulo del ancho del héroe desde donde empezó el
   * dash hasta su posición actual (al terminar cubre todo el recorrido), en
   * lugar del arco de `range`/`arc`.
   */
  sweep: boolean;
  /** Mientras dura la habilidad, los proyectiles enemigos que lo tocan rebotan (Reflector de Falco). */
  reflectProjectiles: boolean;
  /** Giro máximo (rad/s) hacia el cursor durante la habilidad, sin moverse (aliento de Bowser). 0 = no gira. */
  turnToAim: number;
}

export interface ProjectileSpec {
  kind: ProjectileKind;
  speed: number;
  damage: number;
  knockback: number;
  hitstun: number;
  radius: number;
  ttl: number;
  /** number of projectiles fired per cast */
  count: number;
  /** radians between projectiles when count > 1 */
  spread: number;
  /** how many extra enemies a projectile can pierce (0 = stops at the first) */
  pierce: number;
  explodeRadius: number;
  explodeDamage: number;
  explodeKnockback: number;
  explodeHitstun: number;
  explodeOnExpire: boolean;
  explodeOnHit: boolean;
  /** tick del lanzamiento en el que sale el proyectil (0 = al instante) */
  fireAtTick: number;
  /** volver a pulsar la tecla detona el proyectil antes de tiempo */
  detonateOnRecast: boolean;
  /** como `StrikeDef.stun`: sólo entonces `hitstun`/`explodeHitstun` aturden */
  stun: boolean;
}

export interface BlinkSpec {
  distance: number;
  invulnTicks: number;
  damage: number;
  radius: number;
  knockback: number;
  hitstun: number;
}

/** Escudo de espinas: un buff sobre el propio héroe que devuelve parte del daño recibido. */
export interface ShieldSpec {
  /** ticks que dura la burbuja (`HeroState.thornsTicks`) */
  durationTicks: number;
  /** fracción del daño recibido (ya reducido por armadura) que se devuelve al atacante */
  reflect: number;
  /** daño que absorbe el escudo al nivel 1 antes de que baje la vida (`HeroState.shieldHp`) */
  absorb: number;
  /** absorción extra por cada nivel del héroe por encima del 1 (ver `shieldAbsorb`) */
  absorbPerLevel: number;
}

/** Absorción del escudo a este nivel del héroe: crece con cada subida de nivel. */
export function shieldAbsorb(spec: ShieldSpec, level: number): number {
  return Math.round(spec.absorb + spec.absorbPerLevel * Math.max(0, level - 1));
}

/**
 * Agarre aéreo (R de Bowser, como en Smash): embiste; si toca a un héroe enemigo
 * lo agarra, salta con él y lo estrella contra el suelo.
 */
export interface GrabSpec {
  dashTicks: number;
  dashSpeed: number;
  /** distancia extra (además de los radios) a la que agarra durante la embestida */
  reach: number;
  /** ticks en el aire con el rival agarrado antes del estrellón */
  liftTicks: number;
  /** ticks de recuperación tras el estrellón o tras fallar el agarre */
  recoveryTicks: number;
  damage: number;
  knockback: number;
  /** aturdimiento al estrellarlo */
  stun: number;
  /** el estrellón también daña (sin aturdir) a otros enemigos en este radio */
  splashRadius: number;
  splashDamage: number;
}

/**
 * Ejecución con objetivo (R de Link, estilo Justicia Demaciana de Garen): elige
 * un héroe enemigo a su alcance (el fijado o el más cercano al cursor), la
 * Trifuerza lo marca y en `strikeTick` cae una espada gigante del cielo que hace
 * `damage` (× poder de habilidad) más una fracción de la vida que le falta,
 * ignorando la armadura. Sin objetivo válido no se lanza.
 */
export interface ExecuteSpec {
  range: number;
  /** radio alrededor del cursor para elegir al objetivo si no hay uno fijado */
  pickRadius: number;
  /** tick del lanzamiento en el que cae la espada */
  strikeTick: number;
  damage: number;
  /** fracción de la vida que le falta al objetivo, por rango (1..3) */
  missingHp: number[];
  /** el objetivo se escapa si se aleja más que range × leash */
  leash: number;
}

export type AbilityDef =
  | { kind: 'strike'; strike: StrikeDef }
  | {
      kind: 'projectile';
      duration: number;
      cooldownTicks: number;
      projectile: ProjectileSpec;
      /**
       * Cargas (flechas de Link): se puede lanzar hasta `charges` veces seguidas.
       * Con cargas a medias, cada una se recupera en cooldown / charges; al gastar
       * la última entra en el cooldown normal, que devuelve todas.
       */
      charges?: number;
    }
  | { kind: 'blink'; duration: number; cooldownTicks: number; blink: BlinkSpec }
  | { kind: 'shield'; duration: number; cooldownTicks: number; shield: ShieldSpec }
  | { kind: 'grab'; duration: number; cooldownTicks: number; grab: GrabSpec }
  | { kind: 'execute'; duration: number; cooldownTicks: number; execute: ExecuteSpec };

export function abilityDuration(ability: AbilityDef): number {
  return ability.kind === 'strike' ? ability.strike.duration : ability.duration;
}

export function abilityCooldown(ability: AbilityDef): number {
  return ability.kind === 'strike' ? ability.strike.cooldownTicks : ability.cooldownTicks;
}

/** Cargas máximas de la habilidad (1 = sin cargas). */
export function abilityCharges(ability: AbilityDef): number {
  return ability.kind === 'projectile' ? Math.max(1, ability.charges ?? 1) : 1;
}

export interface HeroDef {
  id: HeroId;
  displayName: string;
  maxHp: number;
  speed: number;
  radius: number;
  /** 0..1, fraction of incoming knockback ignored */
  knockbackResist: number;
  /** vida ganada por nivel */
  hpPerLevel: number;
  /**
   * Daño de ataque (AD) ganado por nivel. El AD base es `attacks[0].damage`;
   * el autoataque escala 100% con el AD y las habilidades con ABILITY_AD_SCALING.
   */
  attackDamagePerLevel: number;
  /** armadura: el daño recibido se multiplica por 100 / (100 + armadura) */
  armor: number;
  armorPerLevel: number;
  /** regeneración de vida por segundo (fuera de combate) */
  hpRegen: number;
  /** multiplicador de velocidad de ataque (1 = base) */
  attackSpeed: number;
  /** multiplicador del alcance del autoataque (1 = base) */
  attackRange: number;
  /** aceleración de habilidad: reduce los cooldowns (0 = base) */
  abilityHaste: number;
  /** fracción del daño infligido que se cura (omnisucción) */
  omnivamp: number;
  /** letalidad: penetración plana de armadura */
  lethality: number;
  /** fracción (0..1) de penetración de armadura */
  armorPen: number;
  /** fracción (0..1) de reducción de la duración de control */
  tenacity: number;
  attacks: StrikeDef[];
  /**
   * Tirador: el autoataque dispara un proyectil teledirigido (no falla) al
   * objetivo en lugar de golpear cuerpo a cuerpo. El alcance es `attacks[i].range`.
   */
  ranged?: { projectile: ProjectileKind; speed: number };
  abilities: {
    q: AbilityDef;
    w: AbilityDef;
    e: AbilityDef;
    r: AbilityDef;
  };
}

function strike(
  base: Pick<
    StrikeDef,
    'duration' | 'activeStart' | 'activeEnd' | 'range' | 'arc' | 'damage' | 'knockback' | 'hitstun'
  > &
    Partial<StrikeDef>,
): StrikeDef {
  return {
    cooldownTicks: 0,
    dashTicks: 0,
    dashSpeed: 0,
    invulnTicks: 0,
    rehitEvery: 0,
    heavy: false,
    moveLock: true,
    slamOnContact: false,
    unstoppable: false,
    sweep: false,
    reflectProjectiles: false,
    turnToAim: 0,
    stun: false,
    ...base,
  };
}

/** Ticks de vuelo para recorrer `range` unidades a `speed` u/s (30 ticks por segundo). */
function rangeTtl(range: number, speed: number): number {
  return Math.ceil((range / speed) * 30);
}

/** alcance de las flechas de Link: más o menos hasta salir de la pantalla (vista de ~11 u de medio alto) */
const LINK_ARROW_RANGE = 18;

/** Diámetro del área de ataque de una torre (anillo de alcance 7.5). */
const TOWER_AREA_DIAMETER = 15;

function projectile(
  spec: Pick<ProjectileSpec, 'kind' | 'speed' | 'damage' | 'knockback' | 'hitstun' | 'radius' | 'ttl'> &
    Partial<ProjectileSpec>,
): ProjectileSpec {
  return {
    count: 1,
    spread: 0,
    pierce: 0,
    explodeRadius: 0,
    explodeDamage: 0,
    explodeKnockback: 0,
    explodeHitstun: 0,
    explodeOnExpire: false,
    explodeOnHit: false,
    fireAtTick: 0,
    detonateOnRecast: false,
    stun: false,
    ...spec,
  };
}

const link: HeroDef = {
  id: 'knight',
  displayName: 'Link',
  maxHp: 620,
  speed: 6.2,
  radius: 0.45 * HITBOX_SCALE,
  knockbackResist: 0,
  hpPerLevel: 80,
  attackDamagePerLevel: 2.4,
  armor: 22,
  armorPerLevel: 3.2,
  hpRegen: 2.0,
  attackSpeed: 0.26,
  attackRange: 1.0,
  abilityHaste: 0,
  omnivamp: 0.05,
  lethality: 0,
  armorPen: 0,
  tenacity: 0.1,
  attacks: [
    strike({ duration: 14, activeStart: 4, activeEnd: 7, range: 2.7, arc: 110 * DEG, damage: 18, knockback: 2.5, hitstun: 6 }),
    strike({ duration: 15, activeStart: 4, activeEnd: 8, range: 2.7, arc: 120 * DEG, damage: 18, knockback: 2.5, hitstun: 6 }),
    strike({ duration: 21, activeStart: 7, activeEnd: 11, range: 3.0, arc: 130 * DEG, damage: 28, knockback: 7, hitstun: 11, heavy: true }),
  ],
  abilities: {
    // Arco: tensa y suelta una flecha en línea recta
    q: {
      kind: 'projectile',
      duration: 20,
      cooldownTicks: 270,
      // hasta 3 flechas seguidas; cada una vuelve en 90 ticks (3 s) mientras quede alguna
      charges: 3,
      projectile: projectile({ kind: 'arrow', speed: 32, damage: 30, knockback: 3, hitstun: 7, radius: 0.375, ttl: rangeTtl(LINK_ARROW_RANGE, 32), fireAtTick: 9 }),
    },
    // Giro de espada (estilo Garen): ~2.5 s, puede caminar, daño en área cada 12 ticks
    w: {
      kind: 'strike',
      strike: strike({ duration: 75, activeStart: 4, activeEnd: 72, range: 2.7, arc: 360 * DEG, damage: 14, knockback: 0.5, hitstun: 3, cooldownTicks: 300, rehitEvery: 12, moveLock: false }),
    },
    // Bomba: recorre ~11 m y explota; pulsar E de nuevo la detona antes
    e: {
      kind: 'projectile',
      duration: 14,
      cooldownTicks: 360,
      projectile: projectile({ kind: 'blackbomb', speed: 13, damage: 0, knockback: 0, hitstun: 0, radius: 0.3, ttl: 26, explodeOnExpire: true, explodeRadius: 3, explodeDamage: 38, explodeKnockback: 8, explodeHitstun: 12, detonateOnRecast: true }),
    },
    // Juicio de la Trifuerza (estilo Justicia Demaciana de Garen): la Trifuerza
    // marca a un héroe enemigo y una espada gigante cae sobre él; daño puro que
    // crece con la vida que le falta
    r: {
      kind: 'execute',
      duration: 30,
      cooldownTicks: 1200,
      execute: { range: 6.5, pickRadius: 3, strikeTick: 18, damage: 60, missingHp: [0.25, 0.3, 0.35], leash: 1.5 },
    },
  },
};

const bowser: HeroDef = {
  id: 'barbarian',
  displayName: 'Bowser',
  maxHp: 900,
  speed: 5.2,
  radius: 1.5,
  knockbackResist: 0.5,
  hpPerLevel: 105,
  attackDamagePerLevel: 2.6,
  armor: 32,
  armorPerLevel: 4.2,
  hpRegen: 3.5,
  attackSpeed: 0.22,
  attackRange: 1.0,
  abilityHaste: 0,
  omnivamp: 0,
  lethality: 0,
  armorPen: 0,
  tenacity: 0.3,
  attacks: [
    strike({ duration: 16, activeStart: 5, activeEnd: 9, range: 2.9, arc: 120 * DEG, damage: 26, knockback: 4, hitstun: 8 }),
    strike({ duration: 17, activeStart: 5, activeEnd: 10, range: 2.9, arc: 130 * DEG, damage: 26, knockback: 4, hitstun: 8 }),
    strike({ duration: 24, activeStart: 8, activeEnd: 13, range: 3.2, arc: 140 * DEG, damage: 40, knockback: 9, hitstun: 13, heavy: true }),
  ],
  abilities: {
    // Aliento de fuego: escupe llamas en cono hacia delante; daña en área a todo
    // lo que esté dentro, varias veces (las partículas: `breath` en el manifest)
    // dura ~2.3 s y Bowser puede girar sobre sí mismo hacia el cursor mientras escupe
    q: {
      kind: 'strike',
      strike: strike({ duration: 70, activeStart: 6, activeEnd: 68, range: 6.5, arc: 45 * DEG, damage: 7, knockback: 1, hitstun: 3, cooldownTicks: 210, rehitEvery: 6, turnToAim: 3.5 }),
    },
    // Caparazón de púas: se envuelve 4 s en una burbuja de espinas que absorbe
    // 40 de daño y devuelve el 10% del daño recibido a quien lo golpea
    // (héroes y minions; las torres no)
    w: {
      kind: 'shield',
      // pose de lanzamiento: se mete en el caparazón y gira (el escudo actúa desde el tick 0)
      duration: 20,
      cooldownTicks: 300,
      shield: { durationTicks: 120, reflect: 0.1, absorb: 40, absorbPerLevel: 15 },
    },
    // Golpe de tierra: aturde 1 s a todos los enemigos del área (4 u = 3.2 × 1.25);
    // empuja poco para que se queden dentro
    e: {
      kind: 'strike',
      strike: strike({ duration: 34, activeStart: 12, activeEnd: 20, range: 4.0, arc: 360 * DEG, damage: 30, knockback: 3, hitstun: 30, cooldownTicks: 420, invulnTicks: 8, heavy: true, stun: true }),
    },
    // Agarre aéreo (Smash): embiste, agarra a un héroe, salta con él 1 s y lo
    // estrella contra el suelo (aturde y salpica daño alrededor)
    r: {
      kind: 'grab',
      duration: 10 + 30 + 14,
      cooldownTicks: 1200,
      grab: { dashTicks: 10, dashSpeed: 18, reach: 0.6, liftTicks: 30, recoveryTicks: 14, damage: 60, knockback: 4, stun: 24, splashRadius: 2.6, splashDamage: 25 },
    },
  },
};

const samus: HeroDef = {
  id: 'mage',
  displayName: 'Samus',
  maxHp: 560,
  speed: 5.6,
  radius: 0.42 * HITBOX_SCALE,
  knockbackResist: 0,
  hpPerLevel: 70,
  attackDamagePerLevel: 2.0,
  armor: 18,
  armorPerLevel: 2.6,
  hpRegen: 1.5,
  attackSpeed: 0.25,
  attackRange: 1.0,
  abilityHaste: 20,
  omnivamp: 0.08,
  lethality: 0,
  armorPen: 0,
  tenacity: 0.05,
  attacks: [
    strike({ duration: 15, activeStart: 5, activeEnd: 8, range: 3.3, arc: 80 * DEG, damage: 14, knockback: 2, hitstun: 5 }),
    strike({ duration: 15, activeStart: 5, activeEnd: 8, range: 3.3, arc: 80 * DEG, damage: 14, knockback: 2, hitstun: 5 }),
    strike({ duration: 22, activeStart: 8, activeEnd: 12, range: 3.5, arc: 100 * DEG, damage: 22, knockback: 6, hitstun: 10, heavy: true }),
  ],
  abilities: {
    q: {
      kind: 'projectile',
      duration: 20,
      cooldownTicks: 180,
      projectile: projectile({ kind: 'orb', speed: 34, damage: 40, knockback: 8, hitstun: 14, radius: 0.35, ttl: 70 }),
    },
    w: {
      kind: 'projectile',
      duration: 16,
      cooldownTicks: 300,
      projectile: projectile({ kind: 'missile', speed: 26, damage: 28, knockback: 7, hitstun: 10, radius: 0.32, ttl: 60, explodeOnHit: true, explodeRadius: 1.8, explodeDamage: 12, explodeKnockback: 5, explodeHitstun: 8 }),
    },
    e: {
      kind: 'projectile',
      duration: 16,
      cooldownTicks: 360,
      projectile: projectile({ kind: 'bomb', speed: 6, damage: 4, knockback: 1, hitstun: 3, radius: 0.3, ttl: 40, explodeOnExpire: true, explodeRadius: 2.6, explodeDamage: 26, explodeKnockback: 7, explodeHitstun: 10 }),
    },
    r: {
      kind: 'strike',
      strike: strike({ duration: 34, activeStart: 8, activeEnd: 20, range: 2.6, arc: 360 * DEG, damage: 30, knockback: 10, hitstun: 12, cooldownTicks: 1200, dashTicks: 14, dashSpeed: 14, invulnTicks: 10, heavy: true }),
    },
  },
};

const falco: HeroDef = {
  id: 'falco',
  displayName: 'Falco',
  maxHp: 560,
  speed: 6.8,
  radius: 0.42 * HITBOX_SCALE,
  knockbackResist: 0,
  hpPerLevel: 70,
  attackDamagePerLevel: 2.6,
  armor: 16,
  armorPerLevel: 2.6,
  hpRegen: 1.5,
  attackSpeed: 0.5, // tirador: dispara cada ~1.07 s (los cuerpo a cuerpo golpean cada ~1.8 s)
  attackRange: 1.0,
  abilityHaste: 5,
  omnivamp: 0.05,
  lethality: 6,
  armorPen: 0,
  tenacity: 0.05,
  // Tirador: blaster a distancia moderada (5.5). Contra una torre (radio 1.1)
  // dispara desde 6.6 de su centro: dentro de su alcance (7.5), no puede asediarla de lejos.
  ranged: { projectile: 'blaster', speed: 30 },
  attacks: [
    strike({ duration: 16, activeStart: 6, activeEnd: 6, range: 5.5, arc: 70 * DEG, damage: 15, knockback: 1, hitstun: 0 }),
    strike({ duration: 16, activeStart: 6, activeEnd: 6, range: 5.5, arc: 70 * DEG, damage: 15, knockback: 1, hitstun: 0 }),
    strike({ duration: 20, activeStart: 8, activeEnd: 8, range: 5.5, arc: 70 * DEG, damage: 24, knockback: 3, hitstun: 0, heavy: true }),
  ],
  abilities: {
    // Blaster: ráfaga rápida de dos disparos
    q: {
      kind: 'projectile',
      duration: 12,
      cooldownTicks: 150,
      // alcance: 1.5 veces el diámetro del área de una torre (22.5 u)
      projectile: projectile({ kind: 'blaster', speed: 40, damage: 14, knockback: 1, hitstun: 4, radius: 0.24, ttl: rangeTtl(1.5 * TOWER_AREA_DIAMETER, 40), count: 2, spread: 4 * DEG }),
    },
    // Phantasm: dash frontal que golpea al pasar
    w: {
      kind: 'strike',
      // hitbox: rectángulo del ancho de Falco desde el inicio hasta el final del dash
      strike: strike({ duration: 18, activeStart: 1, activeEnd: 10, range: 1.8, arc: 160 * DEG, damage: 18, knockback: 5, hitstun: 8, cooldownTicks: 300, dashTicks: 9, dashSpeed: 24, sweep: true }),
    },
    // Reflector (Shine): escudo hexagonal de 0.5 s que rebota los proyectiles
    // enemigos; al activarse da un golpe corto alrededor que empuja
    e: {
      kind: 'strike',
      strike: strike({ duration: 15, activeStart: 1, activeEnd: 3, range: 2.2, arc: 360 * DEG, damage: 12, knockback: 9, hitstun: 0, cooldownTicks: 360, invulnTicks: 9, reflectProjectiles: true, moveLock: false }),
    },
    // Fire Bird (como la R de Sion): se enciende y carga ~15 m en línea recta,
    // imparable, golpeando una vez a cada enemigo que atraviesa
    r: {
      kind: 'strike',
      strike: strike({ duration: 38, activeStart: 3, activeEnd: 34, range: 1.9, arc: 360 * DEG, damage: 42, knockback: 8, hitstun: 0, cooldownTicks: 1200, dashTicks: 34, dashSpeed: 14, invulnTicks: 6, heavy: true, unstoppable: true }),
    },
  },
};

const mario: HeroDef = {
  id: 'mario',
  displayName: 'Mario',
  maxHp: 660,
  speed: 6.0,
  radius: 0.44 * HITBOX_SCALE,
  knockbackResist: 0.1,
  hpPerLevel: 85,
  attackDamagePerLevel: 2.4,
  armor: 24,
  armorPerLevel: 3.4,
  hpRegen: 2.5,
  attackSpeed: 0.26,
  attackRange: 1.0,
  abilityHaste: 5,
  omnivamp: 0.06,
  lethality: 0,
  armorPen: 0,
  tenacity: 0.15,
  attacks: [
    strike({ duration: 13, activeStart: 4, activeEnd: 6, range: 2.4, arc: 100 * DEG, damage: 17, knockback: 2.5, hitstun: 6 }),
    strike({ duration: 13, activeStart: 4, activeEnd: 6, range: 2.4, arc: 100 * DEG, damage: 17, knockback: 2.5, hitstun: 6 }),
    strike({ duration: 20, activeStart: 7, activeEnd: 10, range: 2.7, arc: 130 * DEG, damage: 28, knockback: 8, hitstun: 11, heavy: true }),
  ],
  abilities: {
    // Capa: barrido frontal amplio
    q: {
      kind: 'strike',
      strike: strike({ duration: 20, activeStart: 5, activeEnd: 10, range: 2.8, arc: 170 * DEG, damage: 18, knockback: 8, hitstun: 10, cooldownTicks: 150 }),
    },
    // Super Jump Punch: gancho saltando hacia arriba y adelante (el salto se ve
    // en el cliente: `jump` en el manifest), invulnerable al despegar
    w: {
      kind: 'strike',
      strike: strike({ duration: 30, activeStart: 3, activeEnd: 14, range: 2.3, arc: 160 * DEG, damage: 30, knockback: 11, hitstun: 12, cooldownTicks: 300, dashTicks: 12, dashSpeed: 10, invulnTicks: 8, heavy: true }),
    },
    // Bola de fuego: va botando por el suelo, alcance medio (13 u; la Q de Falco llega a 22.5)
    e: {
      kind: 'projectile',
      duration: 14,
      cooldownTicks: 360,
      projectile: projectile({ kind: 'fire', speed: 16, damage: 30, knockback: 3, hitstun: 7, radius: 0.34, ttl: rangeTtl(13, 16), pierce: 1 }),
    },
    // Tornado: giro de 360° con golpes múltiples
    r: {
      kind: 'strike',
      strike: strike({ duration: 44, activeStart: 6, activeEnd: 40, range: 2.7, arc: 360 * DEG, damage: 11, knockback: 4, hitstun: 6, cooldownTicks: 1200, dashTicks: 30, dashSpeed: 4, rehitEvery: 8, heavy: true }),
    },
  },
};

const sheik: HeroDef = {
  id: 'sheik',
  displayName: 'Sheik',
  maxHp: 580,
  speed: 7.0,
  radius: 0.42 * HITBOX_SCALE,
  knockbackResist: 0,
  hpPerLevel: 72,
  attackDamagePerLevel: 2.6,
  armor: 18,
  armorPerLevel: 2.8,
  hpRegen: 1.5,
  attackSpeed: 0.28,
  attackRange: 1.0,
  abilityHaste: 10,
  omnivamp: 0.05,
  lethality: 14,
  armorPen: 0.12,
  tenacity: 0.1,
  attacks: [
    strike({ duration: 12, activeStart: 3, activeEnd: 6, range: 2.5, arc: 100 * DEG, damage: 16, knockback: 2, hitstun: 5 }),
    strike({ duration: 12, activeStart: 3, activeEnd: 6, range: 2.5, arc: 100 * DEG, damage: 16, knockback: 2, hitstun: 5 }),
    strike({ duration: 19, activeStart: 6, activeEnd: 10, range: 2.8, arc: 120 * DEG, damage: 26, knockback: 6, hitstun: 10, heavy: true }),
  ],
  abilities: {
    q: {
      kind: 'projectile',
      duration: 14,
      cooldownTicks: 150,
      projectile: projectile({ kind: 'needle', speed: 36, damage: 12, knockback: 2, hitstun: 6, radius: 0.22, ttl: 45, count: 3, spread: 0.12 * DEG }),
    },
    w: {
      kind: 'blink',
      duration: 18,
      cooldownTicks: 300,
      blink: { distance: 7, invulnTicks: 8, damage: 0, radius: 0, knockback: 0, hitstun: 0 },
    },
    e: {
      kind: 'strike',
      strike: strike({ duration: 20, activeStart: 6, activeEnd: 12, range: 4.5, arc: 30 * DEG, damage: 22, knockback: 1, hitstun: 12, cooldownTicks: 420, stun: true }),
    },
    r: {
      kind: 'strike',
      strike: strike({ duration: 30, activeStart: 6, activeEnd: 14, range: 2.6, arc: 360 * DEG, damage: 32, knockback: 9, hitstun: 13, cooldownTicks: 1200, invulnTicks: 5, heavy: true }),
    },
  },
};

export const HERO_DEFS: Record<HeroId, HeroDef> = {
  knight: link,
  barbarian: bowser,
  mage: samus,
  falco,
  mario,
  sheik,
};

export const HERO_ORDER: HeroId[] = ['knight', 'barbarian', 'mage', 'falco', 'mario', 'sheik'];

export const getHeroDef = (id: HeroId): HeroDef => HERO_DEFS[id];
